import { FC, ReactNode } from "react";
import {
  AdminSellBook
} from "Types";
import { useUpdateSellBookStatusMutation } from "Services/Admin";
import { Check, Eye, X } from "lucide-react";
import { useNavigate } from "react-router-dom";


interface Props {
  book: AdminSellBook;
  onUpdateStatus: (id: number, status: "Purchased" | "Rejected") => void;
}

const IconButton = ({
  title,
  onClick,
  disabled,
  children,
}: {
  title: string;
  onClick?: () => void;
  disabled?: boolean;
  children: ReactNode;
}) => (
  <button
    title={title}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
    disabled={disabled}
    className={`p-1 rounded ${
      disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);

const SellBooksActions: FC<Props> = ({ book, onUpdateStatus }) => {
  const navigate = useNavigate();
  const [updateStatus, { isLoading }] = useUpdateSellBookStatusMutation();

  const handleStatusUpdate = async (status: "Purchased" | "Rejected") => {
    try {
      await updateStatus({ id: book.id, status }).unwrap();
      onUpdateStatus(book.id, status);
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const isDisabled = book.status != null ? (book.status.toLowerCase() !== "pending" || isLoading) : isLoading;

  return (
    <div className="flex items-center justify-center gap-3">
      <IconButton
        title="View"
        onClick={() => navigate(`/admin/sell-books/view/${book.id}`)}
      >
        <Eye size={16} className="text-blue-600" />
      </IconButton>

      <IconButton
        title="Approve"
        onClick={() => handleStatusUpdate("Purchased")}
        disabled={isDisabled}
      >
        <Check size={16} className={!isDisabled ? "text-green-600" : ""} />
      </IconButton>

      <IconButton
        title="Reject"
        onClick={() => handleStatusUpdate("Rejected")}
        disabled={isDisabled}
      >
        <X size={16} className={!isDisabled ? "text-red-600" : ""} />
      </IconButton>
    </div>
  );
};

export default SellBooksActions;
